'use client';
import { type FetchResult } from '@apollo/client';
import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  compact,
  formatPrice,
  formatTimeFromMinutes,
  HOUR,
  humanizeTier,
  MINUTE,
  pluralize,
  ProfileImage,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import { usePathname, useSearchParams } from 'next/navigation';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  type HourlyOrderInput,
  type Scalars,
  SkuTier,
} from '../../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../../context/AuthContext';
import {
  BUY_COACHING_REGISTER_OPTIONS,
  setLocalCheckoutOrderInputs,
} from '../../../../utils/checkout';
import {
  DEFAULT_ERROR_MESSAGE,
  SUBSCRIBER_DUMMY_COACH,
  SUBSCRIBER_DUMMY_COACH_URN,
  TYPEFORM_URLS,
} from '../../../../utils/constants';
import { type SkuMapSkusByTier } from '../../../../utils/maps/sku-map';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import {
  calculateHourlyTotal,
  DEFAULT_HOURLY_ORDER_MINUTES,
} from '../../../../utils/order';
import {
  LelandSessionStorage,
  PREVIOUS_ROUTE_KEY,
} from '../../../../utils/storage';
import {
  convertUrlSearchParamsToObject,
  getHashUrlString,
} from '../../../../utils/url';
import LoadingIndicator from '../../../common/LoadingIndicator';
import { ErrorMessage, type InputOption } from '../../../forms/Inputs';
import HourMinuteInput from '../../../inputs/HourMinuteInput';
import RadioInput from '../../../inputs/RadioInput';
import { ServicesDescription } from '../../ServicesDescription';

import {
  type BookHourlyCoachingFormCoachFragment,
  type BookHourlyCoachingFormCoachingFocusFragment,
  type BookHourlyCoachingFormCreateOrderMutationVariables,
  useBookHourlyCoachingFormCreateOrderMutation,
  useBookHourlyCoachingFormInitialLoadQuery,
  useBookHourlyCoachingFormUpdateOrderMutation,
} from './__generated-gql-types__/BookHourlyCoachingForm.generated';

interface BookHourlyCoachingFormProps {
  coach: BookHourlyCoachingFormCoachFragment;
  defaultValues: BookHourlyCoachingFormCreateOrderMutationVariables;
  orderUrn?: Scalars['OrderUrn']['output'];
  services: Possible<BookHourlyCoachingFormCoachingFocusFragment[]>;
  onClose: () => void;
}

export const BookHourlyCoachingForm: FC<BookHourlyCoachingFormProps> = ({
  coach,
  defaultValues,
  orderUrn,
  services,
  onClose,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { currentUser, isNewSubscriptionExperience } = useAuth();
  const isNewExperienece = isNewSubscriptionExperience || coach.newExperience;

  const isMd = useMediaQuery(Breakpoints.MD);
  const coachToUse = isNewExperienece ? SUBSCRIBER_DUMMY_COACH : coach;
  const tier = coach.tier;
  const humanizedTier = tier ? humanizeTier(tier) : null;

  const [createOrder] = useBookHourlyCoachingFormCreateOrderMutation();
  const [updateOrder] = useBookHourlyCoachingFormUpdateOrderMutation();

  const { data: initialLoadData, loading: initialLoading } =
    useBookHourlyCoachingFormInitialLoadQuery({
      variables: { coachUrn: coach.id },
    });
  const isSubscriber = !!initialLoadData?.activeCustomerSubscriptions.length;

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BookHourlyCoachingFormCreateOrderMutationVariables>({
    defaultValues,
  });

  const watchTimeInMinute = watch('hourlyOrder.timeInMinute');

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [skusByTier, setSkusByTier] = useState<SkuMapSkusByTier>({
    [SkuTier.BASE]: {
      sessionNumberOptions: [],
    },
    [SkuTier.PRO]: {
      sessionNumberOptions: [],
    },
    [SkuTier.ELITE]: {
      sessionNumberOptions: [],
    },
    [SkuTier.ELITE_PLUS]: {
      sessionNumberOptions: [],
    },
  });

  const hoursOfCoachingOptions: InputOption[] = useMemo(() => {
    return (
      (tier &&
        Object.values(skusByTier[tier].sessionNumberOptions)
          .map((value) => {
            const basePrice = skusByTier[tier].sessionNumberOptions[1].price;
            const originalPrice = basePrice * value.numberOfSessions;
            const discount = originalPrice - value.price;

            return {
              label: (
                <NonSubsRadioButtonLabel
                  label={`${pluralize(value.numberOfSessions, 'hour')} of coaching`}
                  price={formatPrice(value.price)}
                  discount={discount}
                />
              ),
              value: value.numberOfSessions * 60,
            };
          })
          .filter(compact)) ??
      []
    );
  }, [skusByTier, tier]);

  useEffect(() => {
    if (initialLoadData?.skus) {
      setSkusByTier((prev) => {
        const newSkusByTier: SkuMapSkusByTier = { ...prev };
        initialLoadData.skus.results.forEach((sku) => {
          newSkusByTier[sku.tier].sessionNumberOptions[sku.numberOfSessions] =
            sku;
        });
        return newSkusByTier;
      });
    }
  }, [initialLoadData?.skus]);

  const sku = tier
    ? skusByTier[tier].sessionNumberOptions[watchTimeInMinute / 60]
    : null;

  const timeBooked = watchTimeInMinute
    ? formatTimeFromMinutes(watchTimeInMinute)
    : null;

  const hourlyRate = coach.hourlyRate;

  const totalAmount = formatPrice(
    isNewExperienece && !isSubscriber
      ? (sku?.price ?? 0)
      : calculateHourlyTotal(watchTimeInMinute ?? 0, hourlyRate ?? 0),
  );

  useEffect(() => {
    register(
      'hourlyOrder.timeInMinute',
      BUY_COACHING_REGISTER_OPTIONS.TIME_IN_MINUTE,
    );
  }, [register]);

  const manageTypeformHref = getHashUrlString(
    TYPEFORM_URLS.MANAGE_SUBSCRIPTION,
    {
      first_name: currentUser?.firstName,
      last_name: currentUser?.lastName,
      email: currentUser?.email,
      id: currentUser?.applicant?.id,
      referrer: pathname,
      ...convertUrlSearchParamsToObject(searchParams),
    },
  );

  const onSubmitWrapper: SubmitHandler<BookHourlyCoachingFormCreateOrderMutationVariables> =
    useCallback(
      async (values) => {
        try {
          const categoryUrn = values.categoryUrn ?? defaultValues.categoryUrn;

          if (isNewExperienece && tier) {
            const hourlyInput: Optional<HourlyOrderInput> = {
              timeInMinute: watchTimeInMinute,
            };

            setLocalCheckoutOrderInputs({
              coachUrn: SUBSCRIBER_DUMMY_COACH_URN,
              tier,
              hourlyOrder: hourlyInput,
              categoryId: categoryUrn,
            });

            LelandSessionStorage.setItem(PREVIOUS_ROUTE_KEY, pathname);

            await router?.push({
              pathname: '/checkout',
            });
          } else {
            if (currentUser) {
              const { id: coachUrn } = coachToUse;
              const hourlyOrder = {
                timeInMinute:
                  values.hourlyOrder?.timeInMinute ??
                  DEFAULT_HOURLY_ORDER_MINUTES,
              };

              let res: FetchResult;
              let orderId: Possible<string>;

              if (orderUrn) {
                const updateOrderRes = await updateOrder({
                  variables: {
                    id: orderUrn,
                    categoryUrn,
                    hourlyOrder,
                  },
                });

                orderId = updateOrderRes.data?.updateOrder?.id;
                res = updateOrderRes;
              } else {
                const createOrderRes = await createOrder({
                  variables: {
                    coachUrn,
                    hourlyOrder,
                    numberOfCharges: 1,
                    categoryUrn,
                  },
                });

                orderId = createOrderRes.data?.createOrder?.id;
                res = createOrderRes;
              }

              if (!wasMutationSuccessful(res)) {
                setErrorMessage(DEFAULT_ERROR_MESSAGE);
                console.warn(res.errors);
                throw new Error('Failed to create/update order');
              }

              await router?.push(`/checkout?order=${orderId}`);
            } else {
              const hourlyOrder = {
                timeInMinute:
                  values.hourlyOrder?.timeInMinute ??
                  DEFAULT_HOURLY_ORDER_MINUTES,
              };

              setLocalCheckoutOrderInputs({
                coachUrn: coachToUse.id,
                hourlyOrder,
                categoryId: categoryUrn,
              });

              await router?.push('/checkout');
            }
          }
        } catch (e) {
          setErrorMessage(DEFAULT_ERROR_MESSAGE);
          console.warn(e);
        }
      },
      [
        coachToUse,
        createOrder,
        currentUser,
        defaultValues.categoryUrn,
        isNewExperienece,
        orderUrn,
        router,
        tier,
        updateOrder,
        watchTimeInMinute,
      ],
    );

  const hourMinuteInput = (
    <HourMinuteInput
      className="w-full"
      valueInMinutes={watchTimeInMinute ?? 0}
      max={(100 * HOUR) / MINUTE}
      onChange={(timeInMinute) =>
        setValue('hourlyOrder.timeInMinute', timeInMinute)
      }
      error={errors.hourlyOrder?.timeInMinute?.message}
    />
  );

  return initialLoading ? (
    <div className="p-6">
      <LoadingIndicator iconClassName="w-20 h-20" />
    </div>
  ) : (
    <form
      className="flex grow flex-col overflow-hidden"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <div className="flex grow flex-col justify-start overflow-y-auto">
        {isNewExperienece ? null : (
          <div className="flex flex-col items-center justify-between space-y-6 p-6 md:flex-row md:space-x-6 md:space-y-0">
            <ProfileImage className="size-20 shrink-0" {...coachToUse.user} />
            <div className="flex grow flex-col items-start justify-center">
              <h2 className="text-center text-xl font-medium leading-snug text-leland-gray-dark md:text-left">
                Get custom 1-on-1 coaching with {coachToUse.user.firstName}
              </h2>
              <ServicesDescription
                className="mt-1 text-center text-lg leading-snug text-leland-gray-light md:text-left"
                services={services}
              />
            </div>
          </div>
        )}
        <hr />
        <div className="p-6">
          <h3 className="text-lg font-medium text-leland-gray-dark">
            Coaching Time
          </h3>
          <p className="mb-3 text-base text-leland-gray-light">
            How much time would you like to book?
          </p>
          {isSubscriber ? (
            hourMinuteInput
          ) : isNewExperienece ? (
            <div className="divide-y divide-leland-gray-stroke rounded-md border border-leland-gray-stroke">
              {hoursOfCoachingOptions.map((option) => (
                <NonSubsRadioButton
                  key={option.value}
                  option={option}
                  checked={watchTimeInMinute === option.value}
                  onChange={(timeInMinute) =>
                    setValue('hourlyOrder.timeInMinute', timeInMinute)
                  }
                />
              ))}
            </div>
          ) : (
            hourMinuteInput
          )}
          {isNewExperienece && tier !== SkuTier.ELITE_PLUS && humanizedTier ? (
            <div className="mt-3 flex w-full items-center justify-center rounded-lg bg-leland-gray-hover p-4">
              <p className="leading-5 text-leland-gray-light">
                <span className="font-medium text-leland-gray-dark">
                  You are on the {humanizedTier} tier.
                </span>{' '}
                If you want coaching from someone above this tier, you will need
                to{' '}
                <a
                  className="link text-leland-primary"
                  href={manageTypeformHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  upgrade your tier
                </a>
                .
              </p>
            </div>
          ) : null}
        </div>
        <hr />
        <div className="p-6">
          <h3 className="text-lg font-medium text-leland-gray-dark">Summary</h3>
          {watchTimeInMinute ? (
            <p className="mt-3 leading-5 text-leland-gray-light">
              {isNewExperienece
                ? `${timeBooked}${humanizedTier ? ` at the ${humanizedTier} tier` : ''}${isSubscriber ? ` · ${formatPrice(hourlyRate ?? 0)}/hr` : ''}`
                : `You selected ${timeBooked} @ ${formatPrice(hourlyRate ?? 0)}/hr`}
            </p>
          ) : null}
          <p className="mt-2 text-2xl font-medium text-leland-gray-dark">
            {totalAmount}
          </p>
        </div>
      </div>
      <div className="flex w-full flex-col items-end justify-center border-t border-leland-gray-stroke p-4 md:rounded-b-lg">
        <div className="flex w-full md:space-x-2.5">
          {isMd ? (
            <Button
              label="Back"
              size={ButtonSize.LARGE}
              width={ButtonWidth.FULL}
              onClick={onClose}
            />
          ) : null}
          <Button
            type="submit"
            label="Continue to Checkout"
            buttonColor={ButtonColor.PRIMARY}
            size={ButtonSize.LARGE}
            width={ButtonWidth.FULL}
            disabled={isSubmitting}
          />
        </div>
        {errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </div>
    </form>
  );
};

interface NonSubsRadioButtonLabelProps {
  label: string;
  discount?: number;
  price: string;
}

const NonSubsRadioButtonLabel: FC<NonSubsRadioButtonLabelProps> = ({
  label,
  discount,
  price,
}) => {
  return (
    <div className="flex w-full items-center justify-between leading-5">
      <p className="text-leland-gray-dark">{label}</p>
      <div className="flex items-center space-x-2">
        {discount ? (
          <div className="flex items-center justify-center rounded bg-leland-primary/25 px-1.5 py-1 text-sm font-medium text-leland-dark-green">
            Save {formatPrice(discount)}
          </div>
        ) : null}
        <p className="text-leland-gray-light">{price}</p>
      </div>
    </div>
  );
};

interface NonSubsRadioButtonProps {
  option: InputOption;
  checked: boolean;
  onChange: (valueInMinutes: number) => void;
}

const NonSubsRadioButton: FC<NonSubsRadioButtonProps> = ({
  option,
  checked,
  onChange,
}) => {
  return (
    <div className="p-4">
      <RadioInput
        className="mr-4 text-leland-primary"
        labelClassName="items-center text-leland-gray-alt w-full"
        option={option}
        onChange={() => onChange(option.value as number)}
        name="non-subs-hourly"
        checked={checked}
      />
    </div>
  );
};
